'use client'

import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react'
import { AnimatePresence, motion } from 'framer-motion'
import Button from './Button'
import Icon from './Icon'
import { useTranslation } from '../../lib/i18n'

const isValidOpacity = (value) => value >= 0 && value <= 1

const Sheet = ({
  title,
  viewHeight = 50,
  isSheetOpen = false,
  onClose,
  children,
  canClose = true,
  backdropOpacity = 0.85,
  showDismissButton = false,
  dismissLabel = 'Dismiss',
  paddedTop = true,
  scrollable = true,
}) => {
  const { t } = useTranslation()
  if (!isValidOpacity(backdropOpacity)) {
    throw new Error('backdropOpacity must be a number between 0 and 1')
  }

  const paddedTopClass = paddedTop ? 'pt-4' : ''
  const scrollableClass = scrollable ? 'overflow-y-scroll px-4' : 'px-0'

  return (
    <AnimatePresence>
      {isSheetOpen && (
        <Dialog
          open={isSheetOpen}
          onClose={canClose ? onClose : () => {}}
          className="relative z-[105]"
        >
          <DialogBackdrop
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: backdropOpacity }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black ease-in-out duration-300"
          />
          <div className="fixed bottom-0 left-0 right-0 z-10 min-w-screen w-screen overflow-y-auto">
            <motion.div
              drag="y"
              dragConstraints={{ top: 0 }}
              onDragEnd={(event, info) => {
                if (info.offset.y > viewHeight * 2.5 && onClose && canClose) {
                  onClose()
                }
              }}
              dragSnapToOrigin
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="min-w-screen w-screen flex min-h-full items-end justify-center text-center sm:items-center"
            >
              <DialogPanel
                as={motion.div}
                style={{
                  minHeight: `${viewHeight}vh`,
                  height: `${viewHeight}vh`,
                  maxHeight: `${viewHeight}vh`,
                }}
                className="min-w-screen w-screen relative overflow-hidden transform rounded-t-lg bg-white text-left shadow-xl"
              >
                <div className="w-full min-h-full h-full flex flex-col justify-start items-center pt-2">
                  <div className="w-full flex justify-between items-center px-4 pb-1 border-b border-gray-200">
                    <p className="text-base font-semibold leading-5 tracking-tighter text-gray-700">
                      {title}
                    </p>
                    {onClose && (
                      <button
                        onClick={canClose ? onClose : () => {}}
                        className="cursor-pointer"
                        aria-label={t('common.closeModal')}
                      >
                        <Icon name="times" />
                      </button>
                    )}
                  </div>
                  <div
                    className={`w-full h-full max-w-2xl mx-auto justify-items-center ${paddedTopClass} ${scrollableClass}`}
                  >
                    {children}
                    {showDismissButton && (
                      <Button
                        onClick={canClose ? onClose : () => {}}
                        className="w-full my-4"
                      >
                        {dismissLabel || t('common.dismiss')}
                      </Button>
                    )}
                  </div>
                </div>
              </DialogPanel>
            </motion.div>
          </div>
        </Dialog>
      )}
    </AnimatePresence>
  )
}

export default Sheet
